import React, { useEffect } from 'react';
import { Form, Input, Modal, Button, Select, Typography } from 'antd';
import { toast } from 'react-hot-toast';
import createPatient from '../../utils/patient/createPatient';
import updatePatient from '../../utils/patient/updatePatient';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;


export default function PatientForm({
                                      isOpen,
                                      onCancel,
                                      onSave,
                                      editingPatient = null,
                                      buttonStyle = {},
                                    }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const mapFormValuesToAttributes = (values) => ({
    full_name: values.name,
    gender: values.gender,
    email: values.email,
    mobile_number: values.mobile,
    external_id: values.patientId,
    location: values.location,
    dob: values.dob || null,
    link: values.link,
    comments: values.comments
  });

  useEffect(() => {
    if (isOpen && editingPatient) {
      form.setFieldsValue({
        name: editingPatient.getFullName(),
        gender: editingPatient.getGenderValue(),
        email: editingPatient.getEmail(),
        mobile: editingPatient.getMobileNumber(),
        patientId: editingPatient.getExternalId(),
        location: editingPatient.getLocation(),
        link: editingPatient.getLink(),
        dob: editingPatient.dob || '',
        comments: editingPatient.getComments(),
      });
    } else if (isOpen) {
      form.resetFields();
    }
  }, [isOpen, editingPatient, form]);

  const handleSubmit = async (values) => {
    const attributes = mapFormValuesToAttributes(values);

    try {
      if (editingPatient) {
        const updatedPatient = await updatePatient(editingPatient.getUUID(), attributes);
        toast.success(t('common.saved'));
        onSave(updatedPatient, true);
      } else {
        const createdPatient = await createPatient(attributes);
        toast.success(t('common.saved'));
        onSave(createdPatient, false);
      }
    } catch (error) {
      toast.error(t('patient_form.errors.save_failed'));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      forceRender
      title={editingPatient ? t('patient_form.edit_title') : t('patient_form.new_title')}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        validateTrigger="onBlur"
      >
        <Form.Item
          name="name"
          label={<Text type="secondary">{t('patient_form.fields.full_name')}</Text>}
          rules={[{ required: true, message: t('patient_form.errors.full_name_missing') }]}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>

        <Form.Item
          name="gender"
          label={<Text type="secondary">{t('patient_form.fields.gender')}</Text>}
        >
          <Select style={{ fontSize: '16px' }} >
            <Select.Option value="male" style={{ fontSize: '16px' }}>{t('patient_form.fields.gender_male')}</Select.Option>
            <Select.Option value="female" style={{ fontSize: '16px' }}>{t('patient_form.fields.gender_female')}</Select.Option>
            <Select.Option value="non_binary" style={{ fontSize: '16px' }}>{t('patient_form.fields.gender_non_binary')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="dob"
          label={<Text type="secondary">{t('patient_form.fields.dob')}</Text>}
        >
          <Input type="date" style={{ fontSize: '16px' }} />
        </Form.Item>

        <Form.Item
          name="patientId"
          label={<Text type="secondary">{t('patient_form.fields.id')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>

        <Form.Item
          name="location"
          label={<Text type="secondary">{t('patient_form.fields.location')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>

        <Form.Item
          name="email"
          label={<Text type="secondary">{t('patient_form.fields.email')}</Text>}
          rules={[{ type: 'email', message: t('patient_form.errors.invalid_email') }]}
        >
          <Input type="email" style={{ fontSize: '16px' }}/>
        </Form.Item>

        <Form.Item
          name="mobile"
          label={<Text type="secondary">{t('patient_form.fields.mobile_number')}</Text>}
          rules={[
            {
              pattern: /^\+?[0-9\s\-()]{7,20}$/,
              message: t('patient_form.errors.invalid_mobile_number'),
            },
          ]}
        >
          <Input type="tel" style={{ fontSize: '16px' }}/>
        </Form.Item>

        <Form.Item
          name="link"
          label={<Text type="secondary">{t('patient_form.fields.link')}</Text>}
        >
          <Input placeholder="https://myhospital.com/patient/12345" type="url" style={{ fontSize: '16px' }}/>
        </Form.Item>

        <Form.Item
          name="comments"
          label={<Text type="secondary">{t('patient_form.fields.comments')}</Text>}
        >
          <Input.TextArea style={{ fontSize: '16px' }}/>
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          <Button onClick={handleCancel}>{t('patient_form.cancel')}</Button>
          <Button type="primary" htmlType="submit" style={buttonStyle.primary}>
            {editingPatient ? t('patient_form.update') : t('patient_form.create')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}